import React, { useState } from 'react';
import { X, ArrowRight, Loader2 } from 'lucide-react';

// Quiz questions data
const questions = [
  {
    question: "If your shadow had a personality, how would it act?",
    options: [
      { text: "The Overthinking Professor", type: "Documenter", description: "Always analyzing and questioning" },
      { text: "The Emotional Storm", type: "IFS Explorer", description: "Intense feelings that overwhelm" },
      { text: "The Mystical Hermit", type: "Seeker", description: "Seeking deeper spiritual meaning" },
      { text: "The Trapped Rebel", type: "Meditator", description: "Yearning for freedom and authenticity" }
    ]
  },
  {
    question: "How familiar are you with shadow work?",
    options: [
      { text: "I'm new and curious", type: "Seeker", description: "Just beginning to explore" },
      { text: "I've done some but want more structure", type: "Documenter", description: "Looking for organization" },
      { text: "I practice IFS deeply", type: "IFS Explorer", description: "Experienced with parts work" },
      { text: "I use therapy & meditation", type: "Meditator", description: "Seeking integration tools" }
    ]
  },
  {
    question: "What's your biggest obstacle in shadow work?",
    options: [
      { text: "I struggle with consistency & tracking", type: "Documenter", description: "Need better organization" },
      { text: "I have trouble understanding my parts", type: "IFS Explorer", description: "Want clearer inner dialogue" },
      { text: "I want deeper insights into patterns", type: "Seeker", description: "Seeking understanding" },
      { text: "I need structured guidance", type: "Meditator", description: "Looking for direction" }
    ]
  },
  {
    question: "How do you prefer to engage with your inner work?",
    options: [
      { text: "Freewriting & reflection", type: "Documenter", description: "Processing through writing" },
      { text: "Dialogue & conversations", type: "IFS Explorer", description: "Talking with parts" },
      { text: "Spiritual connection", type: "Seeker", description: "Seeking higher guidance" },
      { text: "Structured prompts", type: "Meditator", description: "Following guided practices" }
    ]
  },
  {
    question: "What would make shadow work easier for you?",
    options: [
      { text: "A way to track insights over time", type: "Documenter", description: "Organizing growth" },
      { text: "A tool to visualize my parts", type: "IFS Explorer", description: "Mapping inner system" },
      { text: "AI-guided deep questions", type: "Seeker", description: "Exploring depths" },
      { text: "Integration with practice", type: "Meditator", description: "Combining approaches" }
    ]
  },
  {
    question: "What do you hope to gain from this work?",
    options: [
      { text: "A structured way to document my journey", type: "Documenter", description: "Clear progress tracking" },
      { text: "Better connection with my parts", type: "IFS Explorer", description: "Inner harmony" },
      { text: "Deeper self-exploration", type: "Seeker", description: "Self-discovery" },
      { text: "Integration of practices", type: "Meditator", description: "Unified approach" }
    ]
  },
  {
    question: "How often do you want guidance in your shadow work?",
    options: [
      { text: "Daily prompts & reflections", type: "Documenter", description: "Regular check-ins" },
      { text: "When working with parts", type: "IFS Explorer", description: "As needed for parts work" },
      { text: "Whenever I feel called", type: "Seeker", description: "Following intuition" },
      { text: "Structured weekly practice", type: "Meditator", description: "Regular schedule" }
    ]
  }
];

// Result types and their descriptions
const resultTypes = {
  Documenter: {
    title: "The Documenter",
    description: "You thrive on structure and want to track your growth journey. You'll benefit most from AI-powered journaling and progress tracking.",
    features: [
      "AI-generated summaries of your journey",
      "Emotional trend analysis",
      "Structured templates for consistent practice",
      "Progress visualization tools"
    ],
    cta: "Start documenting your journey with AI-powered insights"
  },
  "IFS Explorer": {
    title: "The IFS Explorer",
    description: "You're drawn to understanding and working with your internal parts. Our IFS-focused tools will help deepen your practice.",
    features: [
      "Interactive parts mapping",
      "AI-guided parts dialogue",
      "Visual system tracking",
      "Integration progress monitoring"
    ],
    cta: "Begin mapping your internal system"
  },
  Seeker: {
    title: "The Seeker",
    description: "You're at the beginning of your shadow work journey, seeking guidance and understanding. We'll help you start with confidence.",
    features: [
      "Guided introduction to shadow work",
      "AI-powered journaling prompts",
      "Basic parts identification",
      "Progress tracking essentials"
    ],
    cta: "Start your guided shadow work journey"
  },
  Meditator: {
    title: "The Meditator",
    description: "You combine various practices and want to integrate shadow work effectively. Our tools will complement your existing practice.",
    features: [
      "Integration with meditation practice",
      "Structured shadow work prompts",
      "Progress tracking across modalities",
      "AI-assisted reflection tools"
    ],
    cta: "Integrate shadow work into your practice"
  }
};

interface QuizModalProps {
  onClose: () => void;
  onComplete: () => void;
}

export default function QuizModal({ onClose, onComplete }: QuizModalProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const handleAnswer = (type: string) => {
    const newAnswers = [...answers, type];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setCalculating(true);
      setTimeout(() => {
        setCalculating(false);
        setShowResult(true);
      }, 1500);
    }
  };

  const getResultType = (answers: string[]) => {
    const counts = answers.reduce((acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(counts).reduce((a, b) => 
      counts[a] > counts[b[0]] ? a : b[0]
    );
  };

  if (calculating) {
    return (
      <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full text-center">
          <div className="flex flex-col items-center space-y-3">
            <Loader2 className="h-10 w-10 text-amber-500 animate-spin" />
            <h3 className="text-lg font-semibold text-midnight-800">
              Analyzing Your Responses...
            </h3>
            <p className="text-sm text-midnight-600">
              Creating your personalized shadow work path
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showResult) {
    const resultType = getResultType(answers);
    const result = resultTypes[resultType as keyof typeof resultTypes];
    
    return (
      <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 shadow-xl max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-600"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-midnight-800 mb-2">
              Your Shadow Work Path:
            </h2>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-marigold-500 bg-clip-text text-transparent">
              {result.title}
            </h3>
          </div>

          <div className="space-y-6 mb-6">
            <p className="text-midnight-700">
              {result.description}
            </p>

            <div className="bg-midnight-50 rounded-lg p-4">
              <h4 className="font-semibold text-midnight-800 mb-3">
                Key Features for Your Journey:
              </h4>
              <ul className="space-y-2">
                {result.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-midnight-700">
                    <span className="text-amber-500">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full bg-gradient-to-r from-amber-500 to-marigold-500 text-white py-3 px-6 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-lg font-medium flex items-center justify-center space-x-2"
          >
            <span>{result.cta}</span>
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 shadow-xl max-w-lg w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-midnight-800">
              Discover Your Path
            </h2>
            <span className="text-sm text-midnight-500">
              {currentQuestion + 1}/{questions.length}
            </span>
          </div>
          
          <div className="w-full bg-midnight-100 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-amber-500 to-marigold-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        <div>
          <h3 className="text-base font-medium text-midnight-800 mb-3">
            {questions[currentQuestion].question}
          </h3>
          
          <div className="space-y-2">
            {questions[currentQuestion].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option.type)}
                className="w-full text-left p-4 rounded-lg border-2 border-mint-200 hover:border-amber-500 hover:bg-amber-50 transition-all"
              >
                <div className="font-medium text-midnight-800">
                  {option.text}
                </div>
                <div className="text-sm text-midnight-600 mt-1">
                  {option.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}